'use client'

import Image from 'next/image'
import Master from '@components/Layout/Master'
import Section from '@components/Section/Section'
import { ScrollAnimations } from '../home/components/ScrollAnimations'
import { useLanguage } from '@contexts/LanguageContext'

const ACHIEVEMENTS = [
  { number: '216', label: 'achChildren', desc: 'achChildrenDesc' },
  { number: '20', label: 'achTeachers', desc: 'achTeachersDesc' },
  { number: '2', label: 'achSchools', desc: 'achSchoolsDesc' },
  { number: '2', label: 'achUniversities', desc: 'achUniversitiesDesc' },
  { number: '42%', label: 'achLiteracy', desc: 'achLiteracyDesc' },
  { number: '38%', label: 'achNumeracy', desc: 'achNumeracyDesc' },
] as const

const GOALS_2026 = [
  { number: 'goalChildrenNumber', label: 'goalChildren', desc: 'goalChildrenDesc' },
  { number: 'goalTeachersNumber', label: 'goalTeachers', desc: 'goalTeachersDesc' },
] as const

const DIFFERENCES = [
  { title: 'equitableAccess', desc: 'equitableAccessDesc' },
  { title: 'localTalent', desc: 'localTalentDesc' },
  { title: 'replicableModel', desc: 'replicableModelDesc' },
  { title: 'sustainableApproach', desc: 'sustainableApproachDesc' },
  { title: 'holisticSupport', desc: 'holisticSupportDesc' },
] as const

const AboutPage: React.FC = () => {
  const { t } = useLanguage()

  return (
    <Master>
      <ScrollAnimations />

      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-content">
            <span className="about-label">{t('aboutAccelero')}</span>
            <h1 className="about-title">{t('aboutHeroTitle')}</h1>
            <p className="about-subtitle">{t('aboutHeroSubtitle')}</p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <Section className="our-story-section">
        <div className="container">
          <div className="story-grid">
            <div className="story-content">
              <span className="section-label">{t('ourStory')}</span>
              <h2 className="section-title-large">{t('bornFromExperience')}</h2>
              <p className="story-text">{t('ourStoryText1')}</p>
              <p className="story-text">{t('ourStoryText2')}</p>
              <div className="story-highlight">
                <span className="highlight-icon">🏆</span>
                <div>
                  <h4>{t('dPrizeRecipient')}</h4>
                  <p>{t('dPrizeDesc')}</p>
                </div>
              </div>
            </div>
            <div className="story-image">
              <div className="image-placeholder">
                <span className="placeholder-icon">📚</span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Mission & Vision */}
      <section className="mission-vision-section">
        <div className="container">
          <div className="mv-grid">
            <div className="mv-card mission-card">
              <div className="mv-icon">🎯</div>
              <h3>{t('ourMission')}</h3>
              <p>{t('missionText')}</p>
            </div>
            <div className="mv-card vision-card">
              <div className="mv-icon">🌟</div>
              <h3>{t('ourVision')}</h3>
              <p>{t('visionText')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <Section className="what-we-do-section">
        <div className="container">
          <div className="section-header-center">
            <span className="section-label">{t('whatWeDo')}</span>
            <h2 className="section-title-large">{t('evidenceBasedEducation')}</h2>
            <p className="section-description">{t('whatWeDoDesc')}</p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📖</div>
              <h3>{t('afterSchoolPrograms')}</h3>
              <p>{t('afterSchoolDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">👨‍🏫</div>
              <h3>{t('universityVolunteer')}</h3>
              <p>{t('universityVolunteerDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📚</div>
              <h3>{t('literacyRichEnvironments')}</h3>
              <p>{t('literacyRichDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🤝</div>
              <h3>{t('communityPartnerships')}</h3>
              <p>{t('communityPartnershipsDesc')}</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Impact & Goals */}
      <section className="impact-goals-section">
        <div className="container">
          <div className="section-header-center">
            <span className="section-label">{t('ourImpact')}</span>
            <h2 className="section-title-large">{t('aboutImpactTitle')}</h2>
            <p className="section-description">{t('aboutImpactDesc')}</p>
          </div>

          <h3 className="goals-group-title">{t('currentAchievements')}</h3>
          <div className="goals-grid goals-grid-3">
            {ACHIEVEMENTS.map((item) => (
              <div key={item.label} className="goal-card">
                <div className="goal-number">{item.number}</div>
                <div className="goal-label">{t(item.label)}</div>
                <p className="goal-desc">{t(item.desc)}</p>
              </div>
            ))}
          </div>

          <h3 className="goals-group-title">{t('goals2026')}</h3>
          <p className="goals-group-desc">{t('goals2026Desc')}</p>
          <div className="goals-grid goals-grid-2">
            {GOALS_2026.map((item) => (
              <div key={item.label} className="goal-card">
                <div className="goal-number">{t(item.number)}</div>
                <div className="goal-label">{t(item.label)}</div>
                <p className="goal-desc">{t(item.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Different */}
      <Section className="why-different-section">
        <div className="container">
          <div className="section-header-center">
            <span className="section-label">{t('whyDifferent')}</span>
            <h2 className="section-title-large">{t('sustainableCommunityModel')}</h2>
          </div>

          <div className="differences-list">
            {DIFFERENCES.map((item, index) => (
              <div key={item.title} className="difference-item">
                <div className="diff-number">{String(index + 1).padStart(2, '0')}</div>
                <div className="diff-content">
                  <h3>{t(item.title)}</h3>
                  <p>{t(item.desc)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Leadership Team */}
      <section className="team-section">
        <div className="container">
          <div className="section-header-center">
            <span className="section-label">{t('ourTeam')}</span>
            <h2 className="section-title-large">{t('meetTheFounders')}</h2>
            <p className="section-description">{t('passionateEducators')}</p>
          </div>

          <div className="team-grid">
            <div className="team-card">
              <div className="team-image">
                <Image
                  src="/Catherine_Portrait_Formal_1.jpeg"
                  alt="Catherine Octaviane Tikara"
                  width={200}
                  height={200}
                  className="team-photo"
                />
              </div>
              <h3>Catherine Octaviane Tikara</h3>
              <p className="team-role">{t('catherineRole')}</p>
              <p className="team-bio">{t('catherineBio')}</p>
            </div>

            <div className="team-card">
              <div className="team-image">
                <Image
                  src="/kevin_founder.png"
                  alt="Kevin Evannanda Septian"
                  width={200}
                  height={200}
                  className="team-photo"
                />
              </div>
              <h3>Kevin Evannanda Septian</h3>
              <p className="team-role">{t('kevinRole')}</p>
              <p className="team-bio">{t('kevinBio')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta-section">
        <div className="container">
          <div className="cta-card-large">
            <h2>{t('joinTheMovement')}</h2>
            <p>{t('joinMovementDesc')}</p>
            <div className="cta-buttons-group">
              <a href="/donate" className="btn-cta-primary-large">
                {t('supportOurMission')}
              </a>
              <a href="/volunteer" className="btn-cta-secondary-large">
                {t('becomeVolunteer')}
              </a>
              <a href="/contact" className="btn-cta-outline-large">
                {t('getInTouch')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </Master>
  )
}

export default AboutPage
